/**
 * Runtime brand-color override. Writes CSS custom properties to the
 * document root so Tailwind utilities like `bg-primary` (which read
 * `--color-primary`) and per-tenant theming pick it up immediately.
 *
 * Persisted under `localStorage.kss.brand` so the choice survives reloads.
 */

const STORAGE_KEY = 'kss.brand';

export interface BrandColors {
  /** Primary base — e.g. `#7c3aed`. */
  primary: string;
  /** Optional darker shade for hover. Defaults to a 10% darkening. */
  primaryHover?: string;
  /** Optional foreground that contrasts with the primary. Defaults to `#ffffff`. */
  primaryFg?: string;
  /** Optional very-light tint used for primary-light backgrounds. */
  primaryLight?: string;
}

function clampHex(input: string): string {
  const t = input.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(t)) return t;
  if (/^#[0-9a-f]{3}$/.test(t)) {
    return '#' + t.slice(1).split('').map((c) => c + c).join('');
  }
  return '#7c3aed';
}

function shade(hex: string, percent: number): string {
  const safe = clampHex(hex);
  const n = parseInt(safe.slice(1), 16);
  const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 0xff) * (1 + percent))));
  const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 0xff) * (1 + percent))));
  const b = Math.max(0, Math.min(255, Math.round((n & 0xff) * (1 + percent))));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

function hexToRgb(hex: string): [number, number, number] {
  const safe = clampHex(hex);
  const n = parseInt(safe.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export function setBrandColor(colors: BrandColors | string): void {
  if (typeof document === 'undefined') return;
  const cfg: BrandColors =
    typeof colors === 'string' ? { primary: colors } : { ...colors };
  cfg.primary = clampHex(cfg.primary);
  cfg.primaryHover = clampHex(cfg.primaryHover ?? shade(cfg.primary, -0.1));
  cfg.primaryLight = clampHex(cfg.primaryLight ?? shade(cfg.primary, 0.85));
  cfg.primaryFg = clampHex(cfg.primaryFg ?? '#ffffff');

  const root = document.documentElement;
  root.style.setProperty('--color-primary', cfg.primary);
  root.style.setProperty('--color-primary-hover', cfg.primaryHover);
  root.style.setProperty('--color-primary-light', cfg.primaryLight);
  root.style.setProperty('--color-primary-fg', cfg.primaryFg);

  const [r, g, b] = hexToRgb(cfg.primary);
  root.style.setProperty('--color-primary-rgb', `${r} ${g} ${b}`);

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    // private mode — ignore.
  }
}

export function loadPersistedBrandColor(): BrandColors | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    if (raw.startsWith('#')) return { primary: clampHex(raw) };
    const parsed = JSON.parse(raw) as Partial<BrandColors>;
    if (!parsed.primary) return null;
    return { primary: clampHex(parsed.primary), ...parsed };
  } catch {
    return null;
  }
}

/** Apply a previously persisted brand on app boot. Safe to call before `<App />`. */
export function applyPersistedBrandColor(): void {
  const persisted = loadPersistedBrandColor();
  if (persisted) setBrandColor(persisted);
}
